/**
 * A phone number, entered as a country and a local number, stored as E.164.
 *
 * Every phone column in the database is checked against `^\+[1-9][0-9]{6,14}$`,
 * so a free-text box is a trap: somebody types 0722 000 000 the way they would
 * say it, and the insert fails with a constraint error that means nothing to
 * them. Choosing the country separately makes the +254 visible rather than
 * something you are expected to know to type.
 *
 * The flags are plain unicode rather than images — no asset to load, and they
 * inherit the text colour and size like any other character.
 */
export interface Country {
  iso: string;
  name: string;
  dial: string;
  flag: string;
  /** What a local number looks like here, shown as the placeholder. */
  sample: string;
}

// Kenya first: it is where the clients are. The rest are the neighbours we
// expect next, plus the two we are most often called from.
export const COUNTRIES: Country[] = [
  { iso: "KE", name: "Kenya",         dial: "+254", flag: "🇰🇪", sample: "722 000 000" },
  { iso: "UG", name: "Uganda",        dial: "+256", flag: "🇺🇬", sample: "712 345 678" },
  { iso: "TZ", name: "Tanzania",      dial: "+255", flag: "🇹🇿", sample: "712 345 678" },
  { iso: "RW", name: "Rwanda",        dial: "+250", flag: "🇷🇼", sample: "788 123 456" },
  { iso: "ET", name: "Ethiopia",      dial: "+251", flag: "🇪🇹", sample: "911 234 567" },
  { iso: "ZA", name: "South Africa",  dial: "+27",  flag: "🇿🇦", sample: "82 123 4567" },
  { iso: "NG", name: "Nigeria",       dial: "+234", flag: "🇳🇬", sample: "802 123 4567" },
  { iso: "GB", name: "United Kingdom",dial: "+44",  flag: "🇬🇧", sample: "7400 123456" },
  { iso: "US", name: "United States", dial: "+1",   flag: "🇺🇸", sample: "202 555 0147" },
];

/** Split a stored E.164 number back into a country and the local part. */
export function splitE164(value: string): { country: Country; local: string } {
  const match = [...COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => value.startsWith(c.dial));
  if (!match) return { country: COUNTRIES[0]!, local: value.replace(/^\+/, "") };
  return { country: match, local: value.slice(match.dial.length) };
}

/**
 * Local digits and a dial code, joined. A leading zero is dropped: people
 * write their own number as 0722… and the trunk prefix has no place in E.164.
 */
export function toE164(country: Country, local: string): string {
  const digits = local.replace(/\D/g, "").replace(/^0+/, "");
  return digits ? `${country.dial}${digits}` : "";
}

export function isE164(value: string): boolean {
  return /^\+[1-9][0-9]{6,14}$/.test(value);
}

export function PhoneInput({
  id,
  value,
  onChange,
  required,
}: {
  id: string;
  /** E.164, or "" when empty. */
  value: string;
  onChange: (e164: string) => void;
  required?: boolean;
}) {
  const { country, local } = splitE164(value);
  const complete = value === "" || isE164(value);

  return (
    <>
      <div className="phone-row">
        <select
          aria-label="Country"
          value={country.iso}
          onChange={(e) => {
            const next = COUNTRIES.find((c) => c.iso === e.target.value) ?? COUNTRIES[0]!;
            onChange(toE164(next, local));
          }}
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>
              {c.flag} {c.dial} {c.name}
            </option>
          ))}
        </select>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          required={required}
          placeholder={country.sample}
          value={local}
          onChange={(e) => onChange(toE164(country, e.target.value))}
        />
      </div>
      {!complete && (
        <p className="field-hint">
          That does not look complete yet — a {country.name} number is about{" "}
          {country.sample.replace(/\D/g, "").length} digits after {country.dial}.
        </p>
      )}
    </>
  );
}
